export type FoodTypeId =
  | "soup-stew"
  | "curry"
  | "stir-fry"
  | "tray-bake-casserole"
  | "pasta-noodles"
  | "grain-bowl"
  | "salad"
  | "sandwich-wrap";

export type CookingLocationId = "stovetop" | "oven" | "no-cook";

export type IngredientCategory =
  "produce" | "protein" | "base" | "richness" | "flavor-builder" | "finisher";

export const DIET_TAGS = ["vegetarian", "vegan"] as const;
export type DietTag = (typeof DIET_TAGS)[number];

export const ALLERGENS = [
  "celery",
  "egg",
  "gluten",
  "lupin",
  "milk",
  "mustard",
  "peanut",
  "sesame",
  "soy",
  "sulfite",
  "tree-nut",
] as const;
export type Allergen = (typeof ALLERGENS)[number];

export const CULINARY_ROLES = [
  "bulk",
  "protein",
  "starch",
  "fat",
  "acid",
  "salt",
  "umami",
  "aroma",
  "heat",
  "sweet",
  "bitter",
  "fresh",
  "crunch",
] as const;
export type CulinaryRole = (typeof CULINARY_ROLES)[number];

export const UNITS = ["g", "ml", "tsp", "tbsp", "piece", "pinch"] as const;
export type Unit = (typeof UNITS)[number];

export const LOCALES = ["en", "tr"] as const;
export type Locale = (typeof LOCALES)[number];

export interface NutrientData {
  kcal: number;
  proteinG: number;
  carbG: number;
  fatG: number;
  fiberG: number;
  per: number;
  fdcId: number;
  fetchedAt: string;
}

// ADR-0002: translations are optional during incremental rollout. Once every
// ingredient/recipe in the catalog has a reviewed `tr` value (tracked in
// docs/editorial-review.md), tighten these to required and let
// scripts/validate-data.ts enforce universal coverage instead of only
// token-parity for the entries that already have one.
export interface IngredientTranslations {
  tr: { name: string; aliases: string[] };
}

export interface IngredientOptionTranslations {
  tr: { preparation?: string; instructionValues?: Record<string, string> };
}

export interface RecipeStepTranslations {
  tr: { template: string };
}

export interface RecipeFamilyTranslations {
  tr: { nameTemplate: string };
}

export interface Ingredient {
  id: string;
  name: string;
  category: IngredientCategory;
  roles: CulinaryRole[];
  aliases: string[];
  dietTags: DietTag[];
  allergens: Allergen[];
  assumedStaple?: boolean;
  translations?: IngredientTranslations;
}

export interface IngredientOption {
  ingredientId: string;
  quantity: number;
  unit: Unit;
  preparation?: string;
  /**
   * Slot-specific instruction fragments interpolated into step templates.
   * Required contract for egg options: `safetyCue` must be set to a doneness
   * phrase (e.g. "cook until whites are set and yolk is your preferred
   * consistency") and at least one step must reference `{slot.safetyCue}`.
   * Validated by `scripts/validate-data.ts`.
   */
  instructionValues?: Record<string, string>;
  editorialPriority: number;
  translations?: IngredientOptionTranslations;
}

export interface RecipeSlot {
  id: string;
  label: string;
  required: boolean;
  canonicalIngredientId: string;
  options: IngredientOption[];
}

export interface RecipeStep {
  id: string;
  template: string;
  translations?: RecipeStepTranslations;
}

export interface RecipeFamily {
  id: string;
  nameTemplate: string;
  foodType: FoodTypeId;
  primaryCookingLocation: CookingLocationId;
  servings: number;
  activeMinutes: number;
  totalMinutes: number;
  slots: RecipeSlot[];
  steps: RecipeStep[];
  targetBalanceRoles: CulinaryRole[];
  editorialPriority: number;
  sourceNote: string;
  translations?: RecipeFamilyTranslations;
}

export interface SelectionState {
  foodTypeIds: FoodTypeId[];
  cookingLocationIds: CookingLocationId[];
  ingredientIds: string[];
  veganOnly: boolean;
}

export interface AppState extends SelectionState {
  recipeId: string | null;
  swaps: Record<string, string>;
}

export interface Candidate {
  recipeId: string;
  slotAssignment: Record<string, string>;
  canonicalMatchCount: number;
  matchedIngredientCount: number;
  missingRequiredSlotCount: number;
  selectedSubstitutionIds: string[];
}

export type IngredientGroup = "selected" | "add" | "pantry" | "optional";

export interface InstantiatedIngredient {
  slotId: string;
  slotLabel: string;
  ingredient: Ingredient;
  option: IngredientOption;
  group: IngredientGroup;
  locked: boolean;
}

export interface InstantiatedRecipe {
  family: RecipeFamily;
  title: string;
  ingredients: InstantiatedIngredient[];
  steps: Array<{ id: string; text: string }>;
  allergens: Allergen[];
  vegan: boolean;
}

export type SuggestionKind = "ingredient" | "food-type" | "cooking-location";

export interface Suggestion {
  kind: SuggestionKind;
  id: string;
  label: string;
  reason: string;
  category?: IngredientCategory;
}

export interface NamedOption<T extends string> {
  id: T;
  label: string;
  shortLabel: string;
  description: string;
}

export const FOOD_TYPES: NamedOption<FoodTypeId>[] = [
  {
    id: "soup-stew",
    label: "Soup & stew",
    shortLabel: "Soup",
    description: "Simmered, spoonable, and comforting",
  },
  {
    id: "curry",
    label: "Curry",
    shortLabel: "Curry",
    description: "Saucy, spiced, and built for spooning",
  },
  {
    id: "stir-fry",
    label: "Stir-fry",
    shortLabel: "Stir-fry",
    description: "Fast vegetables and protein from a hot pan",
  },
  {
    id: "tray-bake-casserole",
    label: "Tray bake",
    shortLabel: "Tray bake",
    description: "One pan, browned edges, little cleanup",
  },
  {
    id: "pasta-noodles",
    label: "Pasta & noodles",
    shortLabel: "Pasta",
    description: "Saucy strands and shapes for a quick meal",
  },
  {
    id: "grain-bowl",
    label: "Grain bowl",
    shortLabel: "Bowl",
    description: "Layered grains, vegetables, and a bold dressing",
  },
  {
    id: "salad",
    label: "Salad",
    shortLabel: "Salad",
    description: "Fresh, layered, and no-cook",
  },
  {
    id: "sandwich-wrap",
    label: "Sandwich & wrap",
    shortLabel: "Wrap",
    description: "Handheld, filling, and easy to adapt",
  },
];

export const COOKING_LOCATIONS: NamedOption<CookingLocationId>[] = [
  {
    id: "stovetop",
    label: "Stovetop",
    shortLabel: "Stove",
    description: "Simmer or saute in a pot or pan",
  },
  {
    id: "oven",
    label: "Oven",
    shortLabel: "Oven",
    description: "Roast or bake with hands-off heat",
  },
  {
    id: "no-cook",
    label: "No-cook",
    shortLabel: "No-cook",
    description: "Chop, combine, and serve",
  },
];

export const INGREDIENT_CATEGORIES: Array<{
  id: IngredientCategory;
  label: string;
  shortLabel: string;
  description: string;
}> = [
  {
    id: "produce",
    label: "Produce",
    shortLabel: "Produce",
    description: "Vegetables and fruit",
  },
  {
    id: "protein",
    label: "Protein",
    shortLabel: "Protein",
    description: "Beans, lentils, tofu, and eggs",
  },
  {
    id: "base",
    label: "Base",
    shortLabel: "Base",
    description: "Grains and hearty starches",
  },
  {
    id: "richness",
    label: "Richness",
    shortLabel: "Rich",
    description: "Oils and creamy body",
  },
  {
    id: "flavor-builder",
    label: "Flavor builders",
    shortLabel: "Flavor",
    description: "Aromatics, spices, and umami",
  },
  {
    id: "finisher",
    label: "Finishers",
    shortLabel: "Finish",
    description: "Acid, herbs, heat, and crunch",
  },
];

export const EMPTY_SELECTION: SelectionState = {
  foodTypeIds: [],
  cookingLocationIds: [],
  ingredientIds: [],
  veganOnly: false,
};

export const EMPTY_APP_STATE: AppState = {
  ...EMPTY_SELECTION,
  recipeId: null,
  swaps: {},
};
